import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { Input } from '../../shared/components';
import Button from '../../shared/components/Button';
import { useAuthStore } from '../../features/auth/store';

// ─── Step 2: Business Profile ─────────────────────────────────────────────────

function StepBusinessProfile({ onNext }: { onNext: () => void }) {
  const [form, setForm] = useState({ storeName: '', description: '', category: '', storeLogoUrl: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.storeName.trim()) { setError('Store name is required'); return; }
    if (!form.description.trim()) { setError('Description is required'); return; }
    if (!form.category.trim()) { setError('Category is required'); return; }
    setLoading(true);
    try {
      // API call would go here: ApiFetch.post('/merchant/profile', form)
      await new Promise(r => setTimeout(r, 600)); // mock delay
      onNext();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-[20px] p-8">
      <div className="mb-6">
        <h1 className="text-[22px] font-[700] text-foreground">Business Profile</h1>
        <p className="text-[14px] text-muted-foreground mt-1">Tell customers about your store.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Store Name"
          required
          placeholder="e.g. Lagos Minimalist Furniture"
          value={form.storeName}
          onChange={e => setForm(f => ({ ...f, storeName: e.target.value }))}
          className="h-[42px]"
        />
        <div>
          <label className="block text-[14px] font-[600] text-foreground mb-1">Description</label>
          <textarea
            required
            rows={3}
            placeholder="Describe your store and what you sell..."
            className="w-full px-3 py-2 border border-border bg-muted text-foreground rounded-[10px] text-[14px] focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring resize-none"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
        </div>
        <Input
          label="Category"
          required
          placeholder="e.g. Fashion & Apparel"
          value={form.category}
          onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
          className="h-[42px]"
        />
        <div>
          <label className="block text-[14px] font-[600] text-foreground mb-1">Store Logo URL <span className="text-muted-foreground font-[400]">(optional)</span></label>
          <Input
            placeholder="https://..."
            value={form.storeLogoUrl}
            onChange={e => setForm(f => ({ ...f, storeLogoUrl: e.target.value }))}
            className="h-[42px]"
          />
        </div>
        {error && <p className="text-destructive text-[13px]">{error}</p>}
        <Button type="submit" loading={loading} className="w-full mt-2">Save & Continue</Button>
      </form>
    </div>
  );
}

// ─── Step 3: Bank & Payouts ───────────────────────────────────────────────────

function StepBankPayouts({ onNext }: { onNext: () => void }) {
  const [currency, setCurrency] = useState<'NGN' | 'USD'>('NGN');
  const [form, setForm] = useState({
    bankCode: '', accountNumber: '', accountName: '', identityVerificationId: '',
    payoutInterval: 'weekly',
    swiftCode: '', routingNumber: '', bankName: '',
  });
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // VAL-002: auto-resolve account name on 10-digit NUBAN
  const handleAccountNumberChange = async (value: string) => {
    setForm(f => ({ ...f, accountNumber: value, accountName: '' }));
    if (currency === 'NGN' && value.length === 10 && /^\d{10}$/.test(value)) {
      setResolving(true);
      try {
        // API call: WatchmanFetch.get(`/bank/resolve?accountNumber=${value}&bankCode=${form.bankCode}`)
        await new Promise(r => setTimeout(r, 800)); // mock delay
        setForm(f => ({ ...f, accountName: 'JOHN DOE (Mock)' }));
      } finally {
        setResolving(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (currency === 'NGN' && !form.accountName) { setError('Account name could not be resolved'); return; }
    setLoading(true);
    try {
      await new Promise(r => setTimeout(r, 600));
      onNext();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-[20px] p-8">
      <div className="mb-6">
        <h1 className="text-[22px] font-[700] text-foreground">Bank & Payout Details</h1>
        <p className="text-[14px] text-muted-foreground mt-1">Where should we send your payouts?</p>
      </div>

      {/* Currency tabs */}
      <div className="flex gap-2 mb-6 p-1 bg-muted rounded-[10px] w-fit">
        {(['NGN', 'USD'] as const).map(c => (
          <button
            key={c}
            type="button"
            onClick={() => { setCurrency(c); setForm(f => ({ ...f, accountNumber: '', accountName: '' })); }}
            className={`px-4 py-1.5 rounded-[8px] text-[13px] font-[600] transition-all ${currency === c ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
          >
            {c === 'NGN' ? '₦ NGN' : '$ USD'}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {currency === 'NGN' ? (
          <>
            <Input label="Bank Code" required placeholder="e.g. 058" value={form.bankCode}
              onChange={e => setForm(f => ({ ...f, bankCode: e.target.value }))} className="h-[42px]" />
            <div>
              <Input label="Account Number (NUBAN)" required placeholder="10-digit account number"
                value={form.accountNumber} onChange={e => handleAccountNumberChange(e.target.value)}
                maxLength={10} className="h-[42px]" />
              {resolving && <p className="text-[12px] text-muted-foreground mt-1">Resolving account name...</p>}
              {form.accountName && (
                <p className="text-[12px] text-green-600 font-[600] mt-1">✓ {form.accountName}</p>
              )}
            </div>
            <div>
              <label className="block text-[14px] font-[600] text-foreground mb-1">Account Name</label>
              <input disabled value={form.accountName}
                placeholder="Auto-filled after account number"
                className="w-full px-3 border border-border bg-muted text-foreground rounded-[10px] h-[42px] text-[14px] cursor-not-allowed opacity-70" />
            </div>
          </>
        ) : (
          <>
            <Input label="Bank Name" required placeholder="e.g. Chase Bank" value={form.bankName}
              onChange={e => setForm(f => ({ ...f, bankName: e.target.value }))} className="h-[42px]" />
            <Input label="SWIFT / BIC Code" required placeholder="e.g. CHASUS33" value={form.swiftCode}
              onChange={e => setForm(f => ({ ...f, swiftCode: e.target.value }))} className="h-[42px]" />
            <Input label="Routing / ABA Number" required placeholder="9-digit routing number" value={form.routingNumber}
              onChange={e => setForm(f => ({ ...f, routingNumber: e.target.value }))} className="h-[42px]" />
            <Input label="Account Number" required placeholder="Your bank account number" value={form.accountNumber}
              onChange={e => setForm(f => ({ ...f, accountNumber: e.target.value }))} className="h-[42px]" />
          </>
        )}

        <Input label="Identity Verification ID (BVN / Tax ID)" required placeholder="e.g. 12345678901"
          value={form.identityVerificationId}
          onChange={e => setForm(f => ({ ...f, identityVerificationId: e.target.value }))} className="h-[42px]" />

        <div>
          <label className="block text-[14px] font-[600] text-foreground mb-1">Payout Interval</label>
          <select
            value={form.payoutInterval}
            onChange={e => setForm(f => ({ ...f, payoutInterval: e.target.value }))}
            className="w-full px-3 border border-border bg-muted text-foreground rounded-[10px] h-[42px] text-[14px] focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Bi-weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        {error && <p className="text-destructive text-[13px]">{error}</p>}
        <Button type="submit" loading={loading} className="w-full mt-2">Save & Continue</Button>
      </form>
    </div>
  );
}

// ─── Step 4: Store Configuration ─────────────────────────────────────────────

function StepStoreConfig({ onNext }: { onNext: () => void }) {
  const [form, setForm] = useState({ storeSlug: '', shippingZones: '', returnPolicy: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.storeSlug.trim()) { setError('Store URL slug is required'); return; }
    setLoading(true);
    try {
      await new Promise(r => setTimeout(r, 600));
      onNext();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-[20px] p-8">
      <div className="mb-6">
        <h1 className="text-[22px] font-[700] text-foreground">Store Configuration</h1>
        <p className="text-[14px] text-muted-foreground mt-1">Set up your store URL, shipping zones, and return policy.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input label="Store URL Slug" required placeholder="e.g. lagos-minimalist"
            value={form.storeSlug}
            onChange={e => setForm(f => ({ ...f, storeSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
            className="h-[42px]" />
          {form.storeSlug && (
            <p className="text-[12px] text-muted-foreground mt-1">
              Your store will be at: <span className="font-[600] text-foreground">rackandbox.shop/{form.storeSlug}</span>
            </p>
          )}
        </div>
        <div>
          <label className="block text-[14px] font-[600] text-foreground mb-1">Shipping Zones <span className="text-muted-foreground font-[400]">(comma-separated)</span></label>
          <Input placeholder="e.g. Lagos, Abuja, London"
            value={form.shippingZones}
            onChange={e => setForm(f => ({ ...f, shippingZones: e.target.value }))}
            className="h-[42px]" />
        </div>
        <div>
          <label className="block text-[14px] font-[600] text-foreground mb-1">Return Policy <span className="text-muted-foreground font-[400]">(optional)</span></label>
          <textarea rows={3} placeholder="e.g. Returns accepted within 14 days..."
            className="w-full px-3 py-2 border border-border bg-muted text-foreground rounded-[10px] text-[14px] focus:outline-none resize-none"
            value={form.returnPolicy}
            onChange={e => setForm(f => ({ ...f, returnPolicy: e.target.value }))} />
        </div>
        {error && <p className="text-destructive text-[13px]">{error}</p>}
        <Button type="submit" loading={loading} className="w-full mt-2">Save & Continue</Button>
      </form>
    </div>
  );
}

// ─── Complete screen ──────────────────────────────────────────────────────────

function OnboardingComplete() {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();

  const goToDashboard = () => {
    if (user) setUser({ ...user, isProfileCompleted: true });
    navigate('/');
  };

  return (
    <OnboardingLayout currentStep={4}>
      <div className="bg-card border border-border rounded-[20px] p-10 flex flex-col items-center text-center gap-5">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17l-5-5" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div>
          <h1 className="text-[24px] font-[700] text-foreground">Your store is live!</h1>
          <p className="text-[14px] text-muted-foreground mt-2 max-w-sm">
            Welcome to Rack & Box. Your merchant account is now active. A confirmation email has been sent to you.
          </p>
        </div>
        <Button onClick={goToDashboard} className="w-full max-w-xs mt-2">Go to Dashboard</Button>
      </div>
    </OnboardingLayout>
  );
}

// ─── Main orchestrator ────────────────────────────────────────────────────────

// Steps 2–4 map to onboarding step IDs 2,3,4 (Business Profile, Bank & Payouts, Store Config).
// Intake fields are TCS-admin-managed (not merchant-facing); the first product is added later
// from the catalogue. So merchant onboarding is just these 3 profile/config steps.
const STEP_COUNT = 3; // steps 2,3,4

export const OnboardingPage = () => {
  const [stepIndex, setStepIndex] = useState(0); // 0=Business Profile, 1=Bank & Payouts, 2=Store Config
  const [complete, setComplete] = useState(false);

  if (complete) return <OnboardingComplete />;

  const currentStepId = stepIndex + 2; // maps to onboarding step 2,3,4,5,6

  const next = () => {
    if (stepIndex >= STEP_COUNT - 1) {
      setComplete(true);
    } else {
      setStepIndex(s => s + 1);
    }
  };

  return (
    <OnboardingLayout currentStep={currentStepId}>
      {stepIndex === 0 && <StepBusinessProfile onNext={next} />}
      {stepIndex === 1 && <StepBankPayouts onNext={next} />}
      {stepIndex === 2 && <StepStoreConfig onNext={next} />}
    </OnboardingLayout>
  );
};
